import {
  AdvancedCombobox,
  Checkbox,
  Icon,
  Link,
  ModalBody,
  ModalHeaderWithActions,
  Switch,
} from '@evertrue/et-components';
import CharacterCounter from 'apps/interactions/components/character-counter';
import AddUser from 'apps/search/components/add-user';
import { TasksContext } from 'apps/tasks/hooks/tasks-context';
import useTasks from 'apps/tasks/hooks/use-tasks';
import { TaskFormSchema } from 'apps/tasks/utils/task-schema';
import { buildTaskDefaults, setTaskPayload } from 'apps/tasks/utils/utils';
import { useIdentity } from 'base/identity-resolver';
import DatePickerDropdown from 'components/calendar/date-picker-dropdown';
import FormField from 'components/forms/form-field';
import { Formik } from 'formik';
import EverTrue from 'app';
import PropTypes from 'prop-types';
import { useContext, useMemo } from 'react';
import TaskContact from './task-contact';

const TaskForm = ({
  contact = {},
  taskObj = {},
  handleClose = () => {},
  optionalStateManagementFunction = () => {},
  openAddToCalendarModal = () => {},
  setTaskFormData = () => {},
  setAddToCalendarDefaultOpen = () => {},
  addToCalendarDefaultOpen = true,
}) => {
  const { user } = useIdentity();
  const { createTask, updateTask, taskActionTypes, parseActionType } = useTasks();
  const { getAndSetTasks = () => {}, taskFilters } = useContext(TasksContext);
  const { id, contact_journey_id, taskDefaults } = buildTaskDefaults(taskObj, user, contact);
  const isNotTaskOwnerOrCreator =
    taskObj.id && taskObj.owner_user_id !== user.id && taskObj.creator_user_id !== user.id;

  const taskIsPaused = taskObj.status === 'PAUSED';

  const disableEdit = taskIsPaused || isNotTaskOwnerOrCreator;

  // RECORD_VIDEO is hidden from new selections unless the beta flag is on. Keep it as a disabled option when editing
  // a task that already has it, so the field can still resolve its label instead of rendering blank.
  const hasRecordVideoOption = taskActionTypes?.some(({ value }) => value === 'RECORD_VIDEO');
  const actionTypeOptions =
    taskObj.action_type === 'RECORD_VIDEO' && !hasRecordVideoOption
      ? [...(taskActionTypes || []), { value: 'RECORD_VIDEO', label: parseActionType('RECORD_VIDEO'), disabled: true }]
      : taskActionTypes;

  const handleSubmit = async (values) => {
    const payload = setTaskPayload(values);
    handleClose();
    setTaskFormData(values);
    if (id) {
      const task = await updateTask(payload, taskObj);
      optionalStateManagementFunction('addTask', { task });
    } else {
      const task = await createTask(payload);
      optionalStateManagementFunction('addTask', { task });
    }
    openAddToCalendarModal();
    getAndSetTasks(taskFilters);
  };

  const formTitle = id ? 'Edit Task' : 'Add Task';

  /** Build cadence badge data from the task object. */
  const cadenceBadge = useMemo(() => {
    const isV2 = taskObj.task_source === 'cadence_v2' && taskObj.cadence_id;
    const is1x = !isV2 && taskObj.contact_journey_id && taskObj.metadata?.journey_id;
    if (!isV2 && !is1x) return null;

    const name = isV2 ? taskObj._cadenceName || null : taskObj.metadata?.journey_name || null;
    const stepNum = isV2 ? taskObj._stepNumber : taskObj.metadata?.step_number;
    const dayNum = taskObj.metadata?.day_number;
    const cadenceId = isV2 ? taskObj.cadence_id : taskObj.metadata?.journey_id;
    const route = isV2 ? `/cadences-v2/${cadenceId}` : `/cadences/${cadenceId}`;

    let detail = '';
    if (stepNum != null) detail += `Step ${stepNum}`;
    if (dayNum != null) detail += `${detail ? ' | ' : ''}Day ${dayNum}`;

    return { name, detail, route, isV2 };
  }, [taskObj]);

  return (
    <Formik
      initialValues={taskDefaults}
      validationSchema={TaskFormSchema}
      onSubmit={(values) => handleSubmit(values)}
      validationOnMount={true}
    >
      {({ handleChange, handleSubmit, values, isValid }) => (
        <>
          <ModalHeaderWithActions
            title={formTitle}
            closeModal={handleClose}
            onSubmit={handleSubmit}
            disableSubmit={!isValid}
          />
          {cadenceBadge && (
            <div className="task-form--cadence-badge-row">
              <button
                type="button"
                className={`task-form--cadence-badge ${cadenceBadge.isV2 ? 'is-v2' : 'is-v1'}`}
                onClick={() => {
                  handleClose();
                  EverTrue.Navigator(cadenceBadge.route, true);
                }}
                title={cadenceBadge.name || 'View Cadence'}
              >
                <Icon iconKit="gt2" icon="cadences" />
                {cadenceBadge.name && <span className="task-form--cadence-badge-name">{cadenceBadge.name}</span>}
                {cadenceBadge.detail && (
                  <span className="task-form--cadence-badge-detail">&middot; {cadenceBadge.detail}</span>
                )}
                <span className="task-form--cadence-badge-version">{cadenceBadge.isV2 ? '2.0' : '1.0'}</span>
              </button>
            </div>
          )}
          <ModalBody scrollable={false}>
            <TaskContact
              targetContact={values.targetContact}
              setTargetContact={handleChange('targetContact')}
              disableDelete={!!contact_journey_id || disableEdit}
            />
            <FormField required label="Task Name" inlineLabel={false} for="taskName">
              <input
                type="text"
                value={values.title}
                placeholder={`Enter the Task Name...`}
                onChange={handleChange('title')}
                disabled={!!contact_journey_id || disableEdit}
                id="taskName"
              />
              <CharacterCounter limit={255} count={values.title.length} />
            </FormField>
            <FormField label="Description" inlineLabel={false} for="description">
              <textarea
                value={values.description}
                placeholder="Add Details..."
                onChange={handleChange('description')}
                disabled={!!contact_journey_id || disableEdit}
                id="description"
              />
              <CharacterCounter limit={2000} count={values.description.length} />
            </FormField>
            <div className="task-options">
              <FormField
                required={!contact_journey_id}
                label="Action Type"
                inlineLabel={false}
                for="actionType"
                useDiv={true}
              >
                <AdvancedCombobox
                  label="action type"
                  options={actionTypeOptions}
                  onChange={handleChange('actionType')}
                  value={values.actionType}
                  placeholder="Select Type..."
                  disabled={!!contact_journey_id || disableEdit}
                />
              </FormField>
              <FormField label="Assignee" inlineLabel={false} useDiv>
                <AddUser
                  handleOnChange={handleChange('owner')}
                  value={values.owner}
                  disabled={!!contact_journey_id || disableEdit}
                />
              </FormField>
            </div>
            <div className="task-options">
              <FormField label="Due Date" inlineLabel={false} useDiv>
                <DatePickerDropdown
                  date={values.current_due_date}
                  onDayClick={handleChange('current_due_date')}
                  triggerClassName={'task-calendar'}
                  chevronClassName={'date-picker-chevron'}
                  allowClear
                  disabled={disableEdit}
                  disabledDays={{ before: new Date() }}
                  dropdownClassName={'task-form-date-picker-dropdown'}
                />
              </FormField>
              <FormField label="Add to Calendar" inlineLabel={false} for={`addToCalendarDefaultOpen-et--onoffswitch`}>
                <Switch
                  disabled={disableEdit}
                  name="addToCalendarDefaultOpen"
                  showLabel={false}
                  checked={addToCalendarDefaultOpen}
                  onChange={() => setAddToCalendarDefaultOpen(!addToCalendarDefaultOpen)}
                />
              </FormField>
            </div>
            {!contact_journey_id && (
              <>
                <Checkbox
                  label="Lock Due Date"
                  name="lockDue"
                  disabled={isNotTaskOwnerOrCreator}
                  value={values.static_due_date}
                  checked={values.static_due_date}
                  onChange={handleChange('static_due_date')}
                >
                  Lock Due Date <Icon icon="lock" />
                </Checkbox>

                <div className="journey-modal--alert">
                  This will prevent any automatic changes to the due date if you choose to
                  <Link href="settings/cadences"> pause your tasks</Link>. You can still change the due date manually at
                  any time.
                </div>
              </>
            )}
          </ModalBody>
        </>
      )}
    </Formik>
  );
};

TaskForm.propTypes = {
  contact: PropTypes.object,
  handleClose: PropTypes.func,
  taskObj: PropTypes.object,
  optionalStateManagementFunction: PropTypes.func,
  openAddToCalendarModal: PropTypes.func,
  setTaskFormData: PropTypes.func,
  setAddToCalendarDefaultOpen: PropTypes.func,
  addToCalendarDefaultOpen: PropTypes.bool,
};

export default TaskForm;
